import React, { PureComponent } from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import {
    Button,
    Modal,
    Loader,
    Text,
    Theme,
    BubbleSelectBlock,
    TextInput,
    PDFViewer,
    MessageCard,
    ContainerControlSite,
    Content,
    ContentBlock,
    Bubble, 
    Files
} from '@simplybenefits/sb-ui-library';
import * as dates from '@simplybenefits/sb-dates';
import Header from '../../components/Header.js';
import { getClaim, stopPayment, resetAdjudication } from '../../actions/adjudication.js';
import { addMemberNote, getUser } from '../../actions/users.js';
import { ArrowBackRounded as ArrowBackIcon } from '@mui/icons-material';
import SingleItemForm from './Forms/SingleItemForm.js';
import MultiItemForm from './Forms/MultiItemForm.js';
import Coverage from '../User/Coverage/index.js';
import Usage from '../User/Usage/index.js';
import Tabs from "@mui/material/Tabs";
import Tab from "@mui/material/Tab";
import Notes from '../User/Profile/Notes.js';
import permissionLevel from '../../services/permissions.js';
import PermissionsComponent from '../../components/PermissionsComponent.js';
import { helpers } from "@simplybenefits/sb-utils";
import AccountNotesForMember from '../User/AccountNotesForMember.js';
import {
    createFolder,
    downloadFile,
    listFiles,
    makePrivate,
    makePublic,
    removeFile,
    uploadFile
} from "../../actions/files.js";
import AdjudicationTimeTracker from './components/AdjudicationTimeTracker.js';

class Adjudicate extends PureComponent {
    state = {
        largeDisplayReceipt: false,
        claimFormat: 'standard',
        claimType: 'standard',
        multiItem: false,
        convertToHsa: false,
        selectedSection: 0,
        selectedPDF: null,
        showAddNote: false,
        note: '',
        loadingData: true,
        dentalStopPaymentConfirmation: false,
        isPendingStatusChange: false
    };

    static getDerivedStateFromProps(props) {
        if (!permissionLevel('Adjudication', props.accessType).includes('read')) {
            window.location.href = '/403';
        }

        return null;
    }

    async componentDidMount() {
        const claimData = await this.props.dispatch(getClaim(this.props.match.params.claimID));

        if (claimData.memberMeta) {
            await this.props.dispatch(getUser(claimData.memberMeta.userID));
        }
        
        if (claimData.meta && claimData.meta.isAutomatedCoordination) {
            this.setState({
                claimFormat: 'cob',
                claimType: claimData.meta.type,
                claim: claimData,
                userData: claimData.memberMeta,
                loadingData: false
            });
        } else {
            this.setState({
                claim: claimData,
                claimType: claimData.meta.type,
                userData: claimData.memberMeta,
                loadingData: false
            });
        }
    }

    _handleTabChange = (event, value) => {
        this.setState({
            selectedSection: value
        });
    };

    _stopPayment = () => {
        this.props.dispatch(stopPayment(this.props.match.params.claimID));
    };

    _resetAdjudication = async () => {
        const success = await this.props.dispatch(resetAdjudication(this.props.match.params.claimID));

        if (success) {
            setTimeout(() => {
                window.location.reload();
            }, 2000);
        }
    };

    _showAddNote = () => {
        this.setState({
            showAddNote: true
        });
    };

    _hideAddNote = () => {
        this.setState({
            showAddNote: false
        });
    };

    _addNote = async () => {
        const success = await this.props.dispatch(addMemberNote(this.props.claim.meta.userID, this.state.note, this.props.match.params.claimID));

        if (success) {
            this.setState({
                showAddNote: false,
                note: ''
            });
        }
    };

    _handleChange = (field, value) => {
        this.setState({
            [field]: value
        });
    };

    _toggleDentalStopPaymentConfirmation = () => {
        this.setState((prevState) => ({
            dentalStopPaymentConfirmation: !prevState.dentalStopPaymentConfirmation
        }));
    };

    _downloadPDF = (base64, filename) => {
        const linkSource = `data:application/pdf;base64,${ base64 }`;
        const downloadLink = document.createElement("a");
        const fileName = `${ filename }.pdf`;

        downloadLink.href = linkSource;
        downloadLink.download = fileName;
        downloadLink.click();
    };

    _isResetAdjudicationVisible = (claim) => {
        const { isEClaim, grandfathered, status, category, payee, telusProcedureId } = claim.meta;

        if (isEClaim || grandfathered) {
            return false; 
        }

        const normalizedStatus = status.toLowerCase();
        const normalizedCategory = category.toLowerCase();
        const normalizedPayee = payee?.toLowerCase();

        const isApprovedOrDeclined = [ 'approved', 'declined' ].includes(normalizedStatus);
        const isPaidDrugFromPractitioner = normalizedStatus === 'paid' && normalizedCategory === 'drugs' && telusProcedureId;
        const isPaidDentalToDentist = normalizedStatus === 'paid' && normalizedCategory === 'dental' && normalizedPayee === 'dentist';

        return isApprovedOrDeclined || isPaidDrugFromPractitioner || isPaidDentalToDentist;
    };

    _headerTitle = (text, styles, claimsBlocked) => {
        const claim = this.props.claim;

        return (
            <div style={ styles.headerTitleBlock }>
                <Button
                    customStyle={ styles.backBtn }
                    onClick={ () => {
                        const url = window.location.href;
                        const userID = url.split('?')[1];

                        if (userID) {
                            window.location.href = `/members/${ userID }`;
                        } else {
                            window.location.href = '/adjudication';
                        }
                    }}
                    transparent
                >
                    <ArrowBackIcon sx={{ padding: 0 }}/>
                </Button>

                <Text
                    extraLarge
                    bold
                    display="block"
                    margin="20px 0"
                >
                    { text }
                </Text>

                <Text
                    medium
                    bold
                    display="block"
                    margin="20px 10px"
                    color={ claim.meta.status.toLowerCase() === 'approved' || claim.meta.status.toLowerCase() === 'referred' ? Theme.colors.brandGreen : claim.meta.status.toLowerCase() === 'declined' ? Theme.colors.red : Theme.colors.mediumDarkBlue }
                >
                    { `${ claim.meta.status.charAt(0).toUpperCase() }${ claim.meta.status.substring(1) }` }
                </Text>

                <div style={{ display: 'flex', flex: 1, flexDirection: 'column', alignItems: 'flex-end' }}>
                    {
                        claim.meta.adjudicatedBy &&
                            <Text
                                small
                                display="block"
                                margin="0 0 10px auto"
                                bold
                            >
                                { `Adjudicated By: ${ claim.meta.adjudicatedBy.toLowerCase() }` }
                            </Text>
                    }

                    {
                        this._isResetAdjudicationVisible(claim) && !claimsBlocked ?
                            <div style={{ display: 'flex', flex: 1, flexDirection: 'row' }}>
                                <PermissionsComponent
                                    permission="Reset Adjudication"
                                    accessType={this.props.accessType}
                                    level="write"
                                > 
                                    <Button
                                        color="primary"
                                        onClick={ this._resetAdjudication }
                                        customStyle={{ display: 'block', marginLeft: 'auto', marginRight: 0.5 }}
                                    >
                                        RESET ADJUDICATION
                                    </Button>
                                </PermissionsComponent>
                            </div>
                            : null
                    }
                </div>
            </div>
        );
    };

    render() {
        const styles = {
            backBtn: {
                marginRight: 1.25,
                minWidth: "40px",
                width: "40px"
            },
            headerBlock: {
                display: 'flex',
                flex: 1,
                flexDirection: 'row'
            },
            section: {
                padding: '20px 0'
            },
            block: {
                display: 'flex',
                flex: 0.8,
                flexDirection: 'column',
                flexWrap: 'wrap'
            },
            receiptBlock: {
                width: '100%'
            },
            supportingBlock: {
                marginRight: 30
            },
            detailsContainer: {
                flex: 1,
                display: 'flex',
                flexDirection: 'row',
                alignItems: 'center',
                padding: '0 50px'
            },
            detailRow: {
                flex: 1,
                display: 'flex',
                flexDirection: 'row',
                padding: '3px 0'
            },
            receipt: {
                height: 'auto',
                width: '150px',
                cursor: 'pointer',
                '&:hover': {
                    boxShadow: '0 10px 20px rgba(0, 0, 0, .30)'
                }
            },
            largeView: {
                width: '600px',
                height: 'auto',
                boxShadow: '0 20px 60px rgba(0, 0, 0, 0.7)',
                marginBottom: 50,
                zIndex: 1000
            },
            confirmDetailsContainer: {
                display: 'flex',
                flexDirection: 'column'
            },
            btnBlock: {
                marginTop: 20,
                display: 'flex',
                flexDirection: 'row',
                justifyContent: 'flex-end',
                alignItems: 'center'
            },
            headerTitleBlock: {
                display: 'flex',
                flex: 1,
                flexDirection: 'row',
                alignItems: 'center'
                // paddingLeft: 50,
                // paddingRight: 50
            },
            sectionHeader: {
                display: 'flex',
                flex: 1,
                flexDirection: 'row',
                alignItems: 'center',
                justifyContent: "space-between",
                borderBottom: "1.5px solid #F1F1F1",
                marginBottom: 10
            },
            modal: {
                width: 1000
            },
            downloadBtnContainer: {
                display: 'flex',
                flex: 1,
                justifyContent: 'center',
                marginBottom: 10
            }
        };

        if (!this.props.claim || !this.state.userData) {
            return <Loader isContainer/>;
        }

        const { claim, accessType, user } = this.props;

        let notes = '';

        if (claim.meta.notes instanceof Array) {
            claim.meta.notes.map((note) => {
                if (note instanceof Object) {
                    notes += `${ note.TEXT }\n`;
                } else {
                    notes += `${ note }\n`;
                }

                return null;
            });
        } else {
            notes = claim.meta.notes;
        }

        const claimsBlocked = this.state.userData.accountClaimsBlocked || this.state.userData.divisionClaimsBlocked || this.state.userData.memberClaimsBlocked;
        // if coverage is terminated for spending accounts, in the individual benefit it will still say benefitActive so we have to check both of these conditions
        const showHsaConversionAndTopOff = user.coverage.spendingAccounts.meta.benefitActive === 1 && user.coverage.spendingAccounts.health.hasBenefit === 1; 

        return (
            <ContainerControlSite background="true">
                <Header
                    subHeader="Adjudication"
                    selectedTitle="adjudication"
                />

                <Content customStyle={{ backgroundColor: 'white', paddingTop: 30 }}>
                    <Tabs
                        value={this.state.selectedSection}
                        indicatorColor="primary"
                        textColor="primary"
                        onChange={this._handleTabChange}
                        variant="scrollable"
                        scrollButtons={true}
                        classes={{
                            root: styles.tabsRoot,
                            indicator: styles.tabsIndicator
                        }}
                        sx={{ paddingLeft: "35px" }}
                    >
                        <Tab
                            label={<Text bold color={ `${ Theme.colors.skyblue }` } customStyle={{ paddingLeft: 40, paddingRight: 40 }}>Claim</Text>}
                            classes={{
                                root: styles.tabRoot,
                                selected: styles.tabSelected
                            }}
                        />

                        {
                            Boolean(claim) && Boolean(claim.meta) && !claim.meta.grandfathered &&
                            [ 'approved', 'declined', 'paid' ].includes(claim.meta.status.toLowerCase()) &&
                            claim.meta.type !== 'hsa' && claim.meta.type !== 'lsa' &&
                                <Tab
                                    label={<Text bold color={ `${ Theme.colors.skyblue }` } customStyle={{ paddingLeft: 40, paddingRight: 40 }}>EOB</Text>}
                                    classes={{
                                        root: styles.tabRoot,
                                        selected: styles.tabSelected
                                    }}
                                />
                        }
                    </Tabs>

                    {
                        this.state.selectedSection === 0 &&
                            <ContentBlock>
                                { this._headerTitle("Claim Overview", styles, claimsBlocked) }

                                {
                                    claimsBlocked
                                        ? (
                                            <MessageCard 
                                                title={"Claims Blocked"}
                                                subtitle={"This member’s claims are currently blocked. This includes claim adjudication and claims submissions from all users and sources."}
                                                isError={true}
                                            />
                                        )
                                        : null
                                }

                                <div style={{ padding: "0 20px" }}>
                                    <AccountNotesForMember
                                        accountID={ this.state.userData.accountID }
                                        classID={ this.state.userData.classID }
                                        divisionID={ this.state.userData.divisionID }
                                    />
                                </div>

                                {
                                    claim?.reimbursement?.reimbursementMethod === 'cheque' ?
                                        <MessageCard
                                            title={ `Member Reimbursement Reimbursement` }
                                            subtitle={`The Simply Benefits Accounting Team has been notified and will issue a cheque directly to the member for reimbursement`}
                                        />
                                        :
                                        <>
                                            { 
                                                claim.reimbursement ?
                                                    <MessageCard
                                                        title={ `Member Reimbursement ${ claim.reimbursement.status.charAt(0).toUpperCase() + claim.reimbursement.status.slice(1) }` }
                                                        subtitle={
                                                            claim.reimbursement.status === 'pending' ?
                                                                `The member reimbursement for $${ helpers.numberWithCommasDecimals(claim.reimbursement.amount) } is pending to be sent tonight.`
                                                                :
                                                                claim.reimbursement.status === 'declined' ?
                                                                    `The member reimbursement was declined as of ${ dates.getLocalDisplay(claim.reimbursement.dateUpdated, 'lll') }`
                                                                    :
                                                                    `The member reimbursement has been ${ claim.reimbursement.status } as of ${ dates.getLocalDisplay(claim.reimbursement.dateUpdated, 'lll') }`
                                                        }
                                                    />
                                                    : null
                                            }
                                        </>
                                }

                                <div style={styles.section}>
                                    <div style={ styles.sectionHeader }>
                                        <Text
                                            large
                                            display="block"
                                            margin="0px 0 10px 0"
                                            color={ Theme.colors.skyBlue }
                                        >
                                            Details
                                        </Text>

                                        {
                                            (claim.meta.category.toUpperCase() !== 'DENTAL') && (claim.isPendingPayment === true) && (claim.meta.status.toLowerCase() === 'approved') && (claim.meta.isEClaim === 0) &&
                                                <Button
                                                    transparent
                                                    onClick={ this._stopPayment }
                                                >
                                                    STOP PAYMENT
                                                </Button>
                                        }
                                        
                                        {
                                            // Stop Payment button is shown for both pay by cheque and pay by EDI due to not being able to distinguish between the two.
                                            (claim.meta.category.toUpperCase() === 'DENTAL') && (claim.isPendingPayment === true) && ((claim.meta.payee?.toUpperCase() === "DENTIST" && claim.meta.status.toUpperCase() === 'PAID') || (claim.meta.payee?.toUpperCase() === "MEMBER" && claim.meta.status.toUpperCase() === 'APPROVED')) &&
                                                <Button
                                                    transparent
                                                    onClick={ this._toggleDentalStopPaymentConfirmation }
                                                >
                                                    STOP PAYMENT
                                                </Button>
                                        }

                                        { 
                                            // Stop EDI Adjustments button is shown for both pay by cheque and pay by EDI due to not being able to distinguish between the two.
                                            (claim.meta.category.toUpperCase() === 'DENTAL' && claim.meta.payee?.toUpperCase() === "DENTIST" && claim.meta.status.toLowerCase() === 'reversed') &&
                                                <Button
                                                    transparent
                                                    onClick={ this._stopPayment }
                                                >
                                                    STOP EDI ADJUSTMENTS
                                                </Button>
                                        }

                                        <Modal
                                            open={ this.state.dentalStopPaymentConfirmation}
                                            onClose={ this._toggleDentalStopPaymentConfirmation }
                                            title={ "Stop Payment Confirmation"}
                                            saveButtonText="Confirm"
                                            primaryButton={{
                                                label: 'Confirm',
                                                onClick: () => {
                                                    this._toggleDentalStopPaymentConfirmation();
                                                    this._stopPayment();
                                                }
                                            }}
                                            secondaryButton={{
                                                label: "Cancel",
                                                onClick: () => {
                                                    this._toggleDentalStopPaymentConfirmation();
                                                }
                                            }}
                                            loader={this.props.loader}
                                        >
                                            <Text medium light>
                                                This will cancel any pending payments for this claim. This cannot be undone. If possible, please reverse the original claim and resubmit.
                                            </Text>
                
                                        </Modal>
                                    </div>

                                    <Text medium formatted margin="20px 50px">{ claim.meta.reasonForFlagging }</Text>

                                    <div style={ styles.detailsContainer}>
                                        <div style={styles.block}>
                                            <div style={ styles.detailRow }>
                                                <Text small bold margin="0 10px 0 0">Category:</Text>
                                                <Text medium light customStyle={{ textTransform: 'capitalize' }} >{ claim.meta.category }</Text>
                                            </div>

                                            <div style={ styles.detailRow }>
                                                <Text small bold margin="0 10px 0 0">Service:</Text>
                                                <Text medium light>{ claim.meta.service }</Text>
                                            </div>

                                            <div style={ styles.detailRow }>
                                                <Text small bold margin="0 10px 0 0">Patient:</Text>
                                                <a href={`/members/${ claim.meta.userID }`} style={{ textDecoration: 'none' }}>
                                                    <Text color={ Theme.colors.skyBlue } medium light>
                                                        { claim.meta.patient }
                                                    </Text>
                                                </a>
                                            </div>

                                            <div style={ styles.detailRow }>
                                                <Text small bold margin="0 10px 0 0">Parent Email:</Text>
                                                <Text medium light>
                                                    { claim.meta.parentEmail }
                                                </Text>
                                            </div>

                                            <div style={ styles.detailRow }>
                                                <Text small bold margin="0 10px 0 0">Type:</Text>
                                                <Text medium light>{ claim.meta.type.charAt(0).toUpperCase() + claim.meta.type.substring(1) }</Text>
                                            </div>

                                            <div style={ styles.detailRow }>
                                                <Text small bold margin="0 10px 0 0">Status:</Text>
                                                <Text medium light>{ claim.meta.status.charAt(0).toUpperCase() + claim.meta.status.substring(1) }</Text>
                                            </div>

                                            <div style={ styles.detailRow }>
                                                <Text small bold margin="0 10px 0 0">Dependent:</Text>
                                                <Text medium light>{ claim.meta.dependent ? 'Yes' : 'No' }</Text>
                                            </div>

                                            {
                                                claim.meta.spouseCoverage && claim.meta.spouseCoverage.employer &&
                                                <div style={ styles.detailRow }>
                                                    <Text small bold margin="0 10px 0 0">Dependent Organization:</Text>
                                                    <Text medium light>{ claim.meta.spouseCoverage.employer }</Text>
                                                </div>
                                            }

                                            {
                                                claim.meta.spouseCoverage && claim.meta.spouseCoverage.insuranceCarrier &&
                                                <div style={ styles.detailRow }>
                                                    <Text small bold margin="0 10px 0 0">Dependent Carrier:</Text>
                                                    <Text medium light>{ claim.meta.spouseCoverage.insuranceCarrier }</Text>
                                                </div>
                                            }

                                            {
                                                claim.meta.spouseCoverage && claim.meta.spouseCoverage.cert &&
                                                <div style={ styles.detailRow }>
                                                    <Text small bold margin="0 10px 0 0">Dependent Cert:</Text>
                                                    <Text medium light>{ claim.meta.spouseCoverage.cert }</Text>
                                                </div>
                                            }
                                        </div>

                                        <div style={styles.block}>
                                            <div style={ styles.detailRow }>
                                                <Text small bold margin="0 10px 0 0">Service Date:</Text>
                                                <Text medium light>{ dates.getStartOfDay(claim.meta.serviceDate, 'll') }</Text>
                                            </div>

                                            <div style={ styles.detailRow }>
                                                <Text small bold margin="0 10px 0 0">Submission Date:</Text>
                                                <Text medium light>{ dates.getLocalDisplay(claim.meta.dateAdded) }</Text>
                                            </div>

                                            <div style={ styles.detailRow }>
                                                <Text small bold margin="0 10px 0 0">Amount Submitted:</Text>
                                                <Text medium light>${ Number(claim.meta.amountSubmitted).toFixed(2) }</Text>
                                            </div>

                                            <div style={ styles.detailRow }>
                                                <Text small bold margin="0 10px 0 0">Amount Paid:</Text>
                                                <Text medium light>${ Number(claim.meta.amountPaid).toFixed(2) }</Text>
                                            </div>

                                            <div style={ styles.detailRow }>
                                                <Text small bold margin="0 10px 0 0">Adjudicated Amount:</Text>
                                                <Text medium light>${ Number(claim.meta.adjudicatedAmount).toFixed(2) }</Text>
                                            </div>

                                            {
                                                claim.meta.type.toLowerCase() === 'standard' &&
                                                <div style={ styles.detailRow }>
                                                    <Text small bold margin="0 10px 0 0">HSA Used:</Text>
                                                    <Text medium light>${ Number(claim.meta.hsaAmount).toFixed(2) }</Text>
                                                </div>
                                            }
                                        </div>
                                    </div>

                                    <div style={ styles.detailsContainer}>
                                        <div style={styles.block}>
                                            <div style={ styles.detailRow }>
                                                <Text small bold margin="0 10px 0 0">Notes:</Text>
                                                <Text medium light formatted>{ notes || 'None' }</Text>
                                            </div>
                                        </div>
                                    </div>

                                    <div style={ styles.detailsContainer}>
                                        <div style={styles.block}>
                                            <div style={ styles.detailRow }>
                                                <Text small bold margin="0 10px 0 0">Member Language:</Text>
                                                <Text medium light>{ claim.memberMeta.language === "fr" ? "French" : "English" }</Text>
                                            </div>
                                        </div>
                                    </div>

                                    {
                                        claim.meta.simplyCOBCoverage &&
                                        <div style={{ ...styles.detailsContainer, marginTop: 10 }}>
                                            <div style={styles.block}>
                                                <div style={ styles.detailRow }>
                                                    <Text medium>
                                                        We have detected that this claim could be automatically coordinated with&nbsp;
                                                        <span style={{ fontWeight: 'bold' }}>{ `${ claim.meta.simplyCOBCoverage.firstname } ${ claim.meta.simplyCOBCoverage.lastname }` }</span>,
                                                        the members dependent&nbsp;
                                                        <span style={{ fontWeight: 'bold' }}>{ `${ claim.meta.simplyCOBCoverage.relationship }` }</span> who also has coverage with Simply Benefits.
                                                        The system will automatically handle this only if the claim format is <span style={{ fontWeight: 'bold' }}>Standard</span> and not a EOB or COB.
                                                    </Text>
                                                </div>
                                            </div>
                                        </div>
                                    }
                                </div>

                                <div style={ styles.section }>
                                    <div style={ styles.sectionHeader }>
                                        <Text
                                            large
                                            display="block"
                                            margin="10px 0"
                                            color={ Theme.colors.skyBlue }
                                        >
                                            Provider
                                        </Text>
                                    </div>

                                    <div style={ styles.detailsContainer}>
                                        <div style={styles.block}>
                                            <div style={ styles.detailRow }>
                                                <Text small bold margin="0 10px 0 0">Status:</Text>
                                                <Text
                                                    medium
                                                    light
                                                    customStyle={{ textTransform: 'capitalize' }}
                                                    color={ claim.meta.provider.isValidated ? '#008000' : '#D30000' }
                                                >
                                                    { claim.meta.provider.isValidated ? 'Validated' : 'Not Validated' }
                                                </Text>
                                            </div>

                                            <div style={ styles.detailRow }>
                                                <Text small bold margin="0 10px 0 0">Name:</Text>
                                                <Text medium light customStyle={{ textTransform: 'capitalize' }} >{ claim.meta.provider.name }</Text>
                                            </div>

                                            {
                                                claim.meta.provider.designation &&
                                                    <div style={ styles.detailRow }>
                                                        <Text small bold margin="0 10px 0 0">Designation:</Text>
                                                        <Text medium light>{ claim.meta.provider.designation }</Text>
                                                    </div>
                                            }

                                            {
                                                claim.meta.provider.licenseNumber &&
                                                    <div style={ styles.detailRow }>
                                                        <Text small bold margin="0 10px 0 0">License Number:</Text>
                                                        <Text medium light>{ claim.meta.provider.licenseNumber }</Text>
                                                    </div>
                                            }

                                            {
                                                claim.meta.provider.address &&
                                                    <div style={ styles.detailRow }>
                                                        <Text small bold margin="0 10px 0 0">Address:</Text>
                                                        <Text medium light>{ claim.meta.provider.address }</Text>
                                                    </div>
                                            }

                                            <div style={ styles.detailRow }>
                                                <Text small bold margin="0 10px 0 0">Validation Method:</Text>
                                                <Text medium light customStyle={{ textTransform: 'capitalize' }} >{ claim.meta.provider.validationMethod || 'N/A' }</Text>
                                            </div>

                                            <div style={ styles.detailRow }>
                                                <Text small bold margin="0 10px 0 0">Validation Notes:</Text>
                                                <Text medium light>{ claim.meta.provider.validationNotes || 'N/A' }</Text>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                <div style={ styles.section }>
                                    <div style={ styles.sectionHeader }>
                                        <Text
                                            large
                                            display="block"
                                            margin="10px 0"
                                            color={ Theme.colors.skyBlue }
                                        >
                                            Files
                                        </Text>
                                    </div>

                                    <Files
                                        isMasterSite={ true }
                                        type="users"
                                        id={ this.props.claim.meta.userID }
                                        parentFolder={ `claims/${ this.props.match.params.claimID }/files` }
                                        dispatch={ this.props.dispatch }
                                        listFiles={ listFiles }
                                        createFolder={ createFolder }
                                        uploadFile={ uploadFile }
                                        downloadFile={ downloadFile }
                                        removeFile={ removeFile }
                                        makePrivate={ makePrivate }
                                        makePublic={ makePublic }
                                        noContainer={ true }
                                        isLocked={ false } // never locked for the Master Site
                                        loader={ this.props.loader }
                                    />
                                </div>

                                <div style={ styles.section }>
                                    <div style={ styles.sectionHeader }>
                                        <Text
                                            large
                                            display="block"
                                            margin="0px 0 10px 0"
                                            color={ Theme.colors.skyBlue }
                                        >
                                            Member Notes
                                        </Text>

                                        <PermissionsComponent
                                            permission="Notes"
                                            accessType={accessType}
                                            level="write"
                                        >
                                            <Button transparent onClick={ this._showAddNote }>
                                                Add Note
                                            </Button>
                                        </PermissionsComponent>
                                    </div>

                                    <div style={{ margin: "0 0 20px 0" }}>
                                        {
                                            this.state.showAddNote &&
                                            <div>
                                                <TextInput
                                                    fullWidth
                                                    value={ this.state.note || '' }
                                                    variant="outlined"
                                                    onChange={(e) => this._handleChange("note", e.target.value)}
                                                    multiline={ true }
                                                    rows={ 5 }
                                                    backgroundColor="#FFFFFF"
                                                    placeholder="Add your note here..."
                                                    margin="0 0 0 0"
                                                />

                                                <div style={{
                                                    display: 'flex',
                                                    flex: 1,
                                                    alignItems: 'flex-start',
                                                    justifyContent: 'flex-end',
                                                    marginBottom: 10
                                                }}>
                                                    <Button transparent onClick={ this._hideAddNote }>
                                                        Close
                                                    </Button>

                                                    <Button transparent onClick={ this._addNote }>
                                                        Save
                                                    </Button>
                                                </div>
                                            </div>
                                        }

                                        <PermissionsComponent
                                            permission="Notes"
                                            accessType={accessType}
                                            level="write"
                                        >
                                            <Notes notes={ claim.memberNotes } isClaimView={ true } />
                                        </PermissionsComponent>
                                    </div>
                                </div>

                                {
                                    (
                                        [ 'pending', 'incomplete', 'suspended' ].includes(claim.meta.status.toLowerCase()) &&
                                        (claim.meta.assignedTo === this.props.master.masterID || permissionLevel('Ignore Assignee', this.props.master.access).includes('write'))
                                    ) &&
                                    <div style={styles.section}>
                                        <div style={ styles.sectionHeader }>
                                            <Text
                                                large
                                                display="block"
                                                margin="10px 0"
                                                color={ Theme.colors.skyBlue }
                                            >
                                                Adjudicate
                                            </Text>
                                        </div>

                                        {
                                            (claim.meta.category.toUpperCase() !== 'DENTAL' || claim.meta.type !== 'standard') &&
                                                <div style={ styles.confirmDetailsContainer }>
                                                    <Text small>Which claim format was submitted?</Text>

                                                    {
                                                        claim.meta.isAutomatedCoordination ?
                                                            <div style={{ width: 100 }}>
                                                                <Bubble
                                                                    label="COB"
                                                                    isSelected={ true }
                                                                />
                                                            </div>
                                                            :
                                                            <BubbleSelectBlock
                                                                first={{
                                                                    title: 'Standard',
                                                                    selected: this.state.claimFormat === 'standard',
                                                                    field: 'standard'
                                                                }}
                                                                second={{
                                                                    title: 'COB',
                                                                    selected: this.state.claimFormat === 'cob',
                                                                    field: 'cob'
                                                                }}
                                                                handleChange={ (field) => {
                                                                    // don't allow changes if the claim has already been approved or declined
                                                                    if (claim.meta.status.toLowerCase() !== 'pending') {
                                                                        return false;
                                                                    }

                                                                    this.setState({ claimFormat: field });
                                                                    return true;
                                                                }}
                                                            />
                                                    }

                                                    <Text small>Does the claim have multiple items?</Text>

                                                    <BubbleSelectBlock
                                                        first={{
                                                            title: 'Yes',
                                                            selected: this.state.multiItem === true,
                                                            field: 'multiItemYes'
                                                        }}
                                                        second={{
                                                            title: 'No',
                                                            selected: this.state.multiItem === false,
                                                            field: 'multiItemNo'
                                                        }}
                                                        handleChange={(field) => {
                                                            // don't allow changes if the claim has already been approved or declined
                                                            if (claim.meta.status.toLowerCase() !== 'pending') {
                                                                return false;
                                                            }

                                                            if (this.state.multiItem === null && field === 'multiItemNo') {
                                                                this.setState({ multiItem: false });
                                                            } else {
                                                                this.setState({ multiItem: !this.state.multiItem });
                                                            }

                                                            return true;
                                                        }}
                                                    />

                                                    {
                                                        (claim.meta.type === 'standard' && !this.state.multiItem && showHsaConversionAndTopOff) &&
                                                            <>
                                                                <Text small>Convert claim to HSA?</Text>

                                                                <BubbleSelectBlock
                                                                    first={{
                                                                        title: 'Yes',
                                                                        selected: this.state.convertToHsa === true,
                                                                        field: 'convertToHsaYes'
                                                                    }}
                                                                    second={{
                                                                        title: 'No',
                                                                        selected: this.state.convertToHsa === false,
                                                                        field: 'convertToHsaNo'
                                                                    }}
                                                                    handleChange={(field) => {
                                                                        // don't allow changes if the claim has already been approved or declined
                                                                        if (claim.meta.status.toLowerCase() !== 'pending') {
                                                                            return false;
                                                                        }

                                                                        if (this.state.convertToHsa === null && field === 'convertToHsaNo') {
                                                                            this.setState({ convertToHsa: false, claimType: 'standard' });
                                                                        } else if (field === 'convertToHsaYes') {
                                                                            this.setState({ convertToHsa: true, claimType: 'hsa' });
                                                                        } else {
                                                                            this.setState({ convertToHsa: false, claimType: 'standard' });
                                                                        }

                                                                        return true;
                                                                    }}
                                                                />
                                                            </>
                                                    }
                                                </div>
                                        }

                                        {
                                            this.state.multiItem ?
                                                <MultiItemForm
                                                    defaults={ claim.meta }
                                                    claimFormat={ this.state.claimFormat }
                                                    claimTypes={ claim.claimTypes }
                                                    claimID={ this.props.match.params.claimID }
                                                    claimsBlocked={claimsBlocked}
                                                    showHsaConversionAndTopOff={showHsaConversionAndTopOff}
                                                    onAdjudicationStart={() => this.setState({ isPendingStatusChange: true })} // immediately prior to sending the claim to the server for approval or decline, change the status so that we can send a final adjudication time update immediately prior to the claim being sent to the server
                                                />
                                                :
                                                <SingleItemForm
                                                    defaults={ claim.meta }
                                                    claimFormat={ this.state.claimFormat }
                                                    claimType={ this.state.claimType }
                                                    claimCategories={ claim.claimTypes }
                                                    claimID={ this.props.match.params.claimID }
                                                    claimsBlocked={claimsBlocked}
                                                    showHsaConversionAndTopOff={showHsaConversionAndTopOff}
                                                    onAdjudicationStart={() => this.setState({ isPendingStatusChange: true })} // immediately prior to sending the claim to the server for approval or decline, change the status so that we can send a final adjudication time update immediately prior to the claim being sent to the server
                                                />
                                        }
                                    </div>
                                }

                                <div style={styles.section}>
                                    <div style={ styles.sectionHeader }>
                                        <Text
                                            large
                                            display="block"
                                            margin="10px 0"
                                            color={ Theme.colors.skyBlue }
                                        >
                                            Usage
                                        </Text>
                                    </div>

                                    <Text
                                        medium
                                        color="red"
                                        margin="0 30px"
                                    >
                                        <span style={{ fontWeight: 'bold' }}>Note: </span>Below is the patients usage including <span style={{ fontWeight: 'bold' }}>Approved, Paid or Referred</span> claims only. To see all usage including pending claims navigate to the members profile usage tab.
                                    </Text>

                                    {
                                        !this.state.loadingData &&
                                            <Usage
                                                usage={claim.usage}
                                                coverage={claim.coverage}
                                                meta={claim.meta}
                                                isAdjudicationView={ true }
                                            />
                                    }
                                </div>

                                {
                                    !claim.meta.grandfathered &&
                                        <div style={styles.section}>
                                            <div style={ styles.sectionHeader }>
                                                <Text
                                                    large
                                                    display="block"
                                                    margin="10px 0"
                                                    color={ Theme.colors.skyBlue }
                                                >
                                                    Coverage
                                                </Text>

                                                <Text
                                                    medium
                                                    color="red"
                                                    margin="0 10px"
                                                >
                                                    <span style={{ fontWeight: 'bold' }}>Note: </span>Below is the patients coverage at time of service.
                                                </Text>
                                            </div>

                                            {
                                                !this.state.loadingData &&
                                                <Coverage
                                                    coverage={claim.coverage}
                                                    meta={claim.coverage?.meta || this.state.userData}
                                                    isAdjudicationView={ true }
                                                    planDesign={null}
                                                />
                                            }
                                        </div>
                                }
                            </ContentBlock>
                    }

                    {
                        this.state.selectedSection === 1 &&
                            <ContentBlock>
                                { this._headerTitle("EOB", styles) }

                                <PDFViewer
                                    pdf={ `data:application/pdf;base64,${ claim.meta.EOB }` }
                                    onDownload={() => {
                                        this._downloadPDF(claim.meta.EOB, `Claim_Summary_${ Math.floor(new Date().getTime() / 1000) }`);
                                    }}
                                    customStyle={{
                                        padding: 20,
                                        backgroundColor: 'grey',
                                        width: 940
                                    }}
                                />
                            </ContentBlock>
                    }
                </Content>
                <span id="adjudicate" />

                {
                    this.props.match?.params?.claimID && claim?.meta?.status
                        ? (
                            <AdjudicationTimeTracker 
                                claimID={this.props.match?.params?.claimID}
                                claimStatus={claim.meta.status}
                                isPendingStatusChange={this.state.isPendingStatusChange}
                            />
                        )
                        : null
                }

            </ContainerControlSite>
        );
    }
}

/**
 *
 */
Adjudicate.propTypes = {
    claim: PropTypes.object,
    accessType: PropTypes.string
};

/**
 *
 * @param {*} state
 */
const mapStateToProps = (state) => ({
    loader: state.loader,
    claim: state.adjudication.currentClaim,
    accessType: state.master.access,
    master: state.master,
    user: state.users.currentUser
});

export default connect(mapStateToProps)(Adjudicate);